import React, { type ComponentType, lazy } from 'react'
import { DashSwitch } from '@retailer-platform/dashboard/routing'
import { DomainRoute } from '../utils/domain/routingComponents'
import { catalogAdminAdminAccessControl } from '../access-control/catalogAdminAdminAccess.configuration'
import { catalogAdminProductEditAccessConfiguration } from '../access-control/catalogAdminProductEditAccess.configuration'
import { catalogAdminImageManagementAccessConfiguration } from '../access-control/catalogAdminImageManagementAccess.configuration'
import { catalogAdminQualityAuditAccessConfiguration } from '../access-control/catalogAdminQualityAuditAccess.configuration'
import {
  catalogAdminMDPlatformAccessConfiguration,
  catalogAdminMDPlatformEditAccessConfiguration,
} from '../access-control/catalogMDPlatformAccess.configuration'
import {
  catalogAdminExtractionModelsAccessConfiguration,
  catalogAdminExtractionModelsEditAccessConfiguration,
} from '../access-control/catalogAdminExtractionModelsAccess.configuration'
import {
  catalogAdminDataBridgeAccessConfiguration,
  catalogAdminDataBridgeEditAccessConfiguration,
} from '../access-control/catalogAdminDataBridgeAccess.configuration'
import { catalogAdminPriceAnomalyTakedownAccessConfiguration } from '../access-control/catalogAdminPriceAnomalyTakedownAccess.configuration'
import { catalogAdminMapleJobDashboardAccessConfiguration } from '../access-control/catalogAdminMapleJobDashboardAccess.configuration'
import {
  catalogAdminSourcePriorityAccessConfiguration,
  catalogAdminSourcePriorityModifyAccessConfiguration,
} from '../access-control/catalogAdminSourcePriorityAccess.configuration'
const CatalogAdminAttributeManagementCreatePage = lazy(
  () =>
    import(
      /* webpackChunkName: "CatalogAdminAttributeManagementCreatePage" */
      '../pages/attribute-management/CatalogAdminAttributeManagementCreatePage'
    )
)
const CatalogAdminAttributeManagementDetailsPage = lazy(
  () =>
    import(
      /* webpackChunkName: "CatalogAdminAttributeManagementDetailsPage" */
      '../pages/attribute-management/CatalogAdminAttributeManagementDetailsPage'
    )
)
const CatalogAdminAttributeManagementEditPage = lazy(
  () =>
    import(
      /* webpackChunkName: "CatalogAdminAttributeManagementEditPage" */
      '../pages/attribute-management/CatalogAdminAttributeManagementEditPage'
    )
)
const CatalogAdminAttributeManagementClonePage = lazy(
  () =>
    import(
      /* webpackChunkName: "CatalogAdminAttributeManagementClonePage" */
      '../pages/attribute-management/CatalogAdminAttributeManagementClonePage'
    )
)

// --------------------
// QAS Page Components
// --------------------

const CatalogAdminQualityAuditWorkflowListPage = lazy(
  () =>
    import(
      /* webpackChunkName: "CatalogAdminQualityAuditWorkflowListPage" */
      '../pages/quality-audit-workflow-list/CatalogAdminQualityAuditWorkflowListPage'
    )
)

const CatalogAdminQualityAuditWorkflowBatchListPage = lazy(
  () =>
    import(
      /* webpackChunkName: "CatalogAdminQualityAuditWorkflowBatchListPage" */
      '../pages/quality-audit-workflow-batch-list/CatalogAdminQualityAuditWorkflowBatchListPage'
    )
)

const CatalogAdminQualityAuditWorkflowTaskListPage = lazy(
  () =>
    import(
      /* webpackChunkName: "CatalogAdminQualityAuditWorkflowTaskListPage" */
      '../pages/quality-audit-workflow-task-list/CatalogAdminQualityAuditWorkflowTaskListPage'
    )
)

const CatalogAdminQualityAuditWorkflowTaskListInputDataPage = lazy(
  () =>
    import(
      /* webpackChunkName: "CatalogAdminQualityAuditWorkflowTaskListInputDataPage" */
      '../pages/quality-audit-workflow-task-list-input-data/CatalogAdminQualityAuditWorkflowTaskListInputDataPage'
    )
)

// --------------------
// Image Management Page Components
// --------------------

const CatalogAdminImageManagementPage = lazy(
  () =>
    import(
      /* webpackChunkName: "CatalogAdminImageManagementPage" */
      '../pages/image-management/CatalogAdminImageManagementPage'
    )
)

// --------------------
// Product Edit Page Components
// --------------------

const CatalogAdminProductEditShowPage = lazy(
  () =>
    import(
      /* webpackChunkName: "CatalogAdminProductEditShowPage" */
      '../pages/product-edit-show/CatalogAdminProductEditShowPage'
    )
)
const CatalogAdminMdPlatformPage = lazy(
  () => import('../pages/md-platform/CatalogAdminMdPlatformPage')
)
const CatalogAdminMdPlatformDetailsPage = lazy(
  () => import('../pages/md-platform-details/CatalogAdminMdPlatformDetailsPage')
)
const CatalogAdminMdPlatformCreatePage = lazy(
  () => import('../pages/md-platform-create/CatalogAdminMdPlatformCreatePage')
)
const CatalogAdminQualityAuditSubmitTasksPage = lazy(
  () => import('../pages/quality-audit-submit-tasks/CatalogAdminQualityAuditSubmitTasksPage')
)
const CatalogAdminAttributeManagementPage = lazy(
  () => import('../pages/attribute-management/CatalogAdminAttributeManagementPage')
)
const CatalogAdminQualityAuditWorkflowModifyPage = lazy(
  () => import('../pages/quality-audit-workflow-modify/CatalogAdminQualityAuditWorkflowModifyPage')
)
const CatalogAdminQualityAuditCreateWorkflowsPage = lazy(
  () =>
    import('../pages/quality-audit-create-workflows/CatalogAdminQualityAuditCreateWorkflowsPage')
)
const CatalogAdminQualityAuditMetricsPage = lazy(
  () => import('../pages/quality-audit-metrics/CatalogAdminQualityAuditMetricsPage')
)
const DataBridgePage = lazy(() => import('../pages/data-bridge/DataBridgePage'))
const DataBridgeCreatePage = lazy(() => import('../pages/data-bridge-create/DataBridgeCreatePage'))
const DataBridgeEditPage = lazy(() => import('../pages/data-bridge-edit/DataBridgeEditPage'))
const DataBridgeBatchListPage = lazy(
  () => import('../pages/data-bridge-batch-list/DataBridgeBatchListPage')
)
const CatalogAdminPriceAnomalyTakedownPage = lazy(
  () =>
    import(
      /* webpackChunkName: "CatalogAdminPriceAnomalyTakedownPage" */
      '../pages/price-anomaly-takedown/CatalogAdminPriceAnomalyTakedownPage'
    )
)
const CatalogAdminPriceAnomalyInvestigationDetailPage = lazy(
  () =>
    import(
      /* webpackChunkName: "CatalogAdminPriceAnomalyInvestigationDetailPage" */
      '../pages/price-anomaly-investigation-detail/CatalogAdminPriceAnomalyInvestigationDetailPage'
    )
)
const CatalogAdminMapleJobDashboardPage = lazy(
  () =>
    import(
      /* webpackChunkName: "CatalogAdminMapleJobDashboardPage" */
      '../pages/maple-job-dashboard/CatalogAdminMapleJobDashboardPage'
    )
)
const CatalogAdminMapleJobDetailsPage = lazy(
  () =>
    import(
      /* webpackChunkName: "CatalogAdminMapleJobDetailsPage" */
      '../pages/maple-job-details/CatalogAdminMapleJobDetailsPage'
    )
)
const CatalogAdminSourcePriorityListPage = lazy(
  () =>
    import(
      /* webpackChunkName: "CatalogAdminSourcePriorityListPage" */
      '../pages/source-priority-list/CatalogAdminSourcePriorityListPage'
    )
)
const CatalogAdminSourcePriorityCreatePage = lazy(
  () =>
    import(
      /* webpackChunkName: "CatalogAdminSourcePriorityCreatePage" */
      '../pages/source-priority-create/CatalogAdminSourcePriorityCreatePage'
    )
)
const CatalogAdminSourcePriorityEditPage = lazy(
  () =>
    import(
      /* webpackChunkName: "CatalogAdminSourcePriorityEditPage" */
      '../pages/source-priority-edit/CatalogAdminSourcePriorityEditPage'
    )
)
const CatalogAdminSourcePriorityImpactAnalysisPage = lazy(
  () =>
    import(
      /* webpackChunkName: "CatalogAdminSourcePriorityImpactAnalysisPage" */
      '../pages/source-priority-impact-analysis/CatalogAdminSourcePriorityImpactAnalysisPage'
    )
)
const CatalogAdminSourcePriorityPendingQueuePage = lazy(
  () =>
    import(
      /* webpackChunkName: "CatalogAdminSourcePriorityPendingQueuePage" */
      '../pages/source-priority-pending-queue/CatalogAdminSourcePriorityPendingQueuePage'
    )
)
// @hygen:inject page-import

// @ts-ignore
export const catalogAdminAdminRouter = (
  <DomainRoute route="app-admin-catalog-admin" accessControl={catalogAdminAdminAccessControl}>
    <DashSwitch>
      <DomainRoute
        exact
        route="app-admin-catalog-admin-product-edit-show"
        component={CatalogAdminProductEditShowPage}
        accessControl={catalogAdminProductEditAccessConfiguration}
      />
      <DomainRoute
        exact
        route="app-admin-catalog-admin-image-management"
        component={CatalogAdminImageManagementPage}
        accessControl={catalogAdminImageManagementAccessConfiguration}
      />
      {/* Quality Audit Routes */}
      <DomainRoute
        exact
        route="app-admin-catalog-admin-quality-audit-workflow-list"
        component={CatalogAdminQualityAuditWorkflowListPage}
        accessControl={catalogAdminQualityAuditAccessConfiguration}
      />
      <DomainRoute
        exact
        route="app-admin-catalog-admin-quality-audit-workflow-batch-list"
        // @ts-ignore
        component={CatalogAdminQualityAuditWorkflowBatchListPage}
        accessControl={catalogAdminQualityAuditAccessConfiguration}
      />
      <DomainRoute
        exact
        route="app-admin-catalog-admin-quality-audit-workflow-task-list"
        component={CatalogAdminQualityAuditWorkflowTaskListPage}
        accessControl={catalogAdminQualityAuditAccessConfiguration}
      />
      <DomainRoute
        exact
        route="app-admin-catalog-admin-quality-audit-workflow-task-list-input-data"
        // @ts-ignore
        component={CatalogAdminQualityAuditWorkflowTaskListInputDataPage}
        accessControl={catalogAdminQualityAuditAccessConfiguration}
      />
      <DomainRoute
        exact
        route="app-admin-catalog-admin-md-platform"
        component={CatalogAdminMdPlatformPage}
        accessControl={catalogAdminMDPlatformAccessConfiguration}
      />
      <DomainRoute
        exact
        route="app-admin-catalog-admin-md-platform-details"
        component={CatalogAdminMdPlatformDetailsPage}
        accessControl={catalogAdminMDPlatformAccessConfiguration}
      />
      <DomainRoute
        exact
        route="app-admin-catalog-admin-md-platform-create"
        component={CatalogAdminMdPlatformCreatePage}
        accessControl={catalogAdminMDPlatformEditAccessConfiguration}
      />
      <DomainRoute
        exact
        route="app-admin-catalog-admin-quality-audit-submit-tasks"
        component={CatalogAdminQualityAuditSubmitTasksPage}
        accessControl={catalogAdminQualityAuditAccessConfiguration}
      />
      <DomainRoute
        exact
        route="app-admin-catalog-admin-quality-audit-workflow-modify"
        component={CatalogAdminQualityAuditWorkflowModifyPage}
        accessControl={catalogAdminQualityAuditAccessConfiguration}
      />
      <DomainRoute
        exact
        route="app-admin-catalog-admin-attribute-management"
        component={CatalogAdminAttributeManagementPage}
        accessControl={catalogAdminExtractionModelsAccessConfiguration}
      />
      <DomainRoute
        exact
        route="app-admin-catalog-admin-quality-audit-create-workflows"
        component={CatalogAdminQualityAuditCreateWorkflowsPage}
        accessControl={catalogAdminQualityAuditAccessConfiguration}
      />
      <DomainRoute
        exact
        route="app-admin-catalog-admin-quality-audit-metrics"
        component={CatalogAdminQualityAuditMetricsPage}
        accessControl={catalogAdminQualityAuditAccessConfiguration}
      />
      <DomainRoute
        exact
        route="app-admin-catalog-admin-attribute-management-create"
        component={CatalogAdminAttributeManagementCreatePage}
        accessControl={catalogAdminExtractionModelsEditAccessConfiguration}
      />
      <DomainRoute
        exact
        route="app-admin-catalog-admin-attribute-management-details"
        component={CatalogAdminAttributeManagementDetailsPage}
        accessControl={catalogAdminExtractionModelsAccessConfiguration}
      />
      <DomainRoute
        exact
        route="app-admin-catalog-admin-attribute-management-edit"
        component={CatalogAdminAttributeManagementEditPage}
        accessControl={catalogAdminExtractionModelsEditAccessConfiguration}
      />
      <DomainRoute
        exact
        route="app-admin-catalog-admin-attribute-management-clone"
        component={CatalogAdminAttributeManagementClonePage}
        accessControl={catalogAdminExtractionModelsEditAccessConfiguration}
      />
      <DomainRoute
        exact
        route="app-admin-catalog-admin-price-anomaly-takedown"
        component={CatalogAdminPriceAnomalyTakedownPage}
        accessControl={catalogAdminPriceAnomalyTakedownAccessConfiguration}
      />
      <DomainRoute
        exact
        route="app-admin-catalog-admin-price-anomaly-investigation-detail"
        component={CatalogAdminPriceAnomalyInvestigationDetailPage as ComponentType<any>}
        accessControl={catalogAdminPriceAnomalyTakedownAccessConfiguration}
      />
      <DomainRoute
        exact
        route="app-admin-catalog-admin-data-bridge"
        component={DataBridgePage}
        accessControl={catalogAdminDataBridgeAccessConfiguration}
      />
      <DomainRoute
        exact
        route="app-admin-catalog-admin-data-bridge-create"
        component={DataBridgeCreatePage}
        accessControl={catalogAdminDataBridgeEditAccessConfiguration}
      />
      <DomainRoute
        exact
        route="app-admin-catalog-admin-data-bridge-edit"
        component={DataBridgeEditPage}
        accessControl={catalogAdminDataBridgeEditAccessConfiguration}
      />
      <DomainRoute
        exact
        route="app-admin-catalog-admin-data-bridge-batch-list"
        component={DataBridgeBatchListPage}
        accessControl={catalogAdminDataBridgeAccessConfiguration}
      />
      {/* MAPLE Job Dashboard Routes */}
      <DomainRoute
        exact
        route="app-admin-catalog-admin-maple-job-dashboard"
        component={CatalogAdminMapleJobDashboardPage}
        accessControl={catalogAdminMapleJobDashboardAccessConfiguration}
      />
      <DomainRoute
        exact
        route="app-admin-catalog-admin-maple-job-details"
        component={CatalogAdminMapleJobDetailsPage as ComponentType<any>}
        accessControl={catalogAdminMapleJobDashboardAccessConfiguration}
      />
      {/* Source Priority Routes */}
      <DomainRoute
        exact
        route="app-admin-catalog-admin-source-priority"
        component={CatalogAdminSourcePriorityListPage}
        accessControl={catalogAdminSourcePriorityAccessConfiguration}
      />
      <DomainRoute
        exact
        route="app-admin-catalog-admin-source-priority-create"
        component={CatalogAdminSourcePriorityCreatePage}
        accessControl={catalogAdminSourcePriorityModifyAccessConfiguration}
      />
      <DomainRoute
        exact
        route="app-admin-catalog-admin-source-priority-edit"
        component={CatalogAdminSourcePriorityEditPage}
        accessControl={catalogAdminSourcePriorityModifyAccessConfiguration}
      />
      <DomainRoute
        exact
        route="app-admin-catalog-admin-source-priority-impact-analysis"
        component={CatalogAdminSourcePriorityImpactAnalysisPage}
        accessControl={catalogAdminSourcePriorityAccessConfiguration}
      />
      <DomainRoute
        exact
        route="app-admin-catalog-admin-source-priority-pending-queue"
        component={CatalogAdminSourcePriorityPendingQueuePage}
        accessControl={catalogAdminSourcePriorityAccessConfiguration}
      />
    </DashSwitch>
  </DomainRoute>
)
